import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ShopifyBulkOperationStatusEnum,
  GetShopifyBulkOperationStatusDocument,
} from '@/lib/graphql/generated/graphql';
import { useQuery } from '@apollo/client/react';
import { Loader2 } from 'lucide-react';

const TERMINAL_STATUSES = new Set<ShopifyBulkOperationStatusEnum>([
  ShopifyBulkOperationStatusEnum.Completed,
  ShopifyBulkOperationStatusEnum.Failed,
  ShopifyBulkOperationStatusEnum.Canceled,
  ShopifyBulkOperationStatusEnum.Expired,
]);
const ERROR_STATUSES = new Set<ShopifyBulkOperationStatusEnum>([
  ShopifyBulkOperationStatusEnum.Failed,
  ShopifyBulkOperationStatusEnum.Canceled,
  ShopifyBulkOperationStatusEnum.Expired,
]);
interface ShopifyBulkProgressProps {
  dealId: string;
  onCompleted: (objectCount: number) => void;
  onError?: () => void;
  accumulatedCount?: number;
}

export const ShopifyBulkProgress: React.FC<ShopifyBulkProgressProps> = ({
  dealId,
  onCompleted,
  onError,
  accumulatedCount,
}) => {
  const { t } = useTranslation();

  const { data, stopPolling } = useQuery(
    GetShopifyBulkOperationStatusDocument,
    {
      variables: { dealId },
      pollInterval: 500,
      fetchPolicy: 'no-cache',
    }
  );

  const bulkStatus = data?.shopifyBulkOperationStatus;
  const objectCount = bulkStatus?.objectCount ?? 0;
  const isTerminal = bulkStatus
    ? TERMINAL_STATUSES.has(bulkStatus.status)
    : false;
  const isError = bulkStatus != null && ERROR_STATUSES.has(bulkStatus.status);

  React.useEffect(() => {
    if (!data) return;
    if (isError) {
      stopPolling();
      onError?.();
    } else if (isTerminal || bulkStatus == null) {
      stopPolling();
      onCompleted(objectCount);
    }
  }, [
    data,
    bulkStatus,
    isTerminal,
    isError,
    onCompleted,
    onError,
    stopPolling,
    objectCount,
  ]);

  return (
    <div className="flex h-[400px] flex-col items-center justify-center gap-4">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
      <div className="text-center">
        <p className="text-sm font-medium">
          {t('general.shopify_import_in_progress')}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('general.shopify_import_in_progress_with_count', {
            count: (accumulatedCount ?? 0) + objectCount,
          })}
        </p>
      </div>
    </div>
  );
};
